import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { MaskedLines, FadeUp, Overline } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";

const SECTIONS = [
  {
    num: "01",
    title: "Keep the Books Clean",
    desc: "Accurate, organized records maintained throughout the year — so you always know where your business stands.",
    items: [
      "Monthly bookkeeping",
      "Bank and credit card reconciliation",
      "Transaction categorization",
      "Financial reporting",
      "Catch-up or cleanup bookkeeping",
    ],
    img: "https://images.unsplash.com/photo-1709880945165-d2208c6ad2ec?q=80&w=1600&auto=format&fit=crop",
    alt: "Calculator and laptop on an organized desk",
  },
  {
    num: "02",
    title: "Keep the Business Compliant",
    desc: "Tax and filing obligations handled on time, at every level — federal, Pennsylvania, and local.",
    items: [
      "Tax preparation",
      "Estimated tax planning",
      "Pennsylvania sales and use tax filing",
      "Payroll support",
      "Related filings",
    ],
    img: "https://static.prod-images.emergentagent.com/jobs/f38635fe-5ba6-4f07-bccf-ef12c441b66a/images/e05211c47ea5141d7317c7b54a4e21834096925f612e4b1832f899b35f967ca0.jpeg",
    alt: "Blank 2026 tax forms on a desk with a pen and reading glasses",
  },
  {
    num: "03",
    title: "Understand the Numbers",
    desc: "Not just recording transactions — understanding what your financial information means for your business.",
    items: [
      "Profit & loss and balance sheet review",
      "Job costing",
      "QuickBooks setup and support",
      "Practical help understanding what your financials mean",
    ],
    img: "https://images.unsplash.com/photo-1648201637025-1c77b9be3013?q=80&w=1600&auto=format&fit=crop",
    alt: "Calculator and pen on financial paperwork",
  },
];

export default function Services() {
  return (
    <div>
      <PageHeader
        testid="services-header"
        overline="Services"
        lines={["Keep the books clean,", "stay compliant,", "understand the numbers."]}
        intro="Monthly bookkeeping, tax preparation, and practical financial help — sized to what your business actually needs."
      />

      <section className="pb-24" data-testid="services-sections">
        <div className="mx-auto max-w-7xl space-y-24 px-6 lg:space-y-32">
          {SECTIONS.map((s, i) => (
            <div key={s.title} className="grid items-center gap-12 lg:grid-cols-12">
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <FadeUp>
                  <div className="relative">
                    <div className="absolute -inset-3 rotate-2 rounded-2xl border border-gold/50" aria-hidden="true" />
                    <img
                      src={s.img}
                      alt={s.alt}
                      className="relative aspect-[4/3] w-full rounded-2xl object-cover shadow-xl"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </FadeUp>
              </div>
              <div className="lg:col-span-7">
                <FadeUp>
                  <span className="font-mono text-sm tracking-[0.3em] text-gold">{s.num}</span>
                  <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                    {s.title}
                  </h2>
                  <p className="mt-4 max-w-xl leading-relaxed text-stone2">{s.desc}</p>
                  <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-sm font-medium text-ink">
                        <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-gold" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </FadeUp>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Marquee />

      <section className="py-24" data-testid="services-software-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="rounded-2xl border border-hairline bg-white p-10 lg:p-14">
            <FadeUp>
              <Overline>Software</Overline>
              <h2 className="mt-5 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
                QuickBooks setup and support, done practically.
              </h2>
              <p className="mt-5 max-w-2xl leading-relaxed text-stone2">
                Whether you're setting up QuickBooks for the first time, cleaning up years of
                backlog, or just want help understanding what the reports are telling you —
                Arnold Bookkeeping provides practical, judgment-free support.
              </p>
              <Link
                to="/contact"
                data-testid="services-software-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-pine px-7 py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-gold hover:text-ink"
              >
                Ask about QuickBooks help <ArrowUpRight size={16} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
